import { CENTERCOMPONENTS } from './CenterComponents';
import { ACTIVETAB } from './RightSideComponents';
import { useMapPanelContext } from './MapPanelContext';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAISMapContext } from '../AISMapContext';
import { useSelectionState } from '../../../components/SelectionStateProvider';

interface GridStyle {
  display: string;
  gridTemplateColumns: string;
  gridTemplateRows: string;
}

export interface GridClasses {
  layerControllerGrid: string;
  metadataGrid: string;
  reportLegendGrid: string;
  reportTableGrid: string;
  targetInfoGrid: string;
  mapPanelGrid: GridStyle;
  replayGrid: string;
  portInfoGrid: string;
}

const useGridClassCalculations = (
  tableExpanded: boolean,
  isSidebarActive: boolean,
  isReplayActive: boolean,
  isTargetPanelActive: boolean,
  isPortPanelActive: boolean,
  multipleTargets: boolean,
  multiplePorts: boolean
) => {
  const { tableHeight } = useMapPanelContext();

  const getLayerControllerGridClass = useCallback(() => {
    let rowStart = 3;
    let rowEnd = 4;

    if (tableExpanded) {
      rowStart = rowStart - 1;
      rowEnd = rowEnd - 1;
    }

    return `col-start-1 col-end-2 row-start-${rowStart} row-end-${rowEnd}`;
  }, [tableExpanded]);

  const getMetadataGridClass = useCallback(() => {
    let colEnd = 7;
    let rowStart = 3;
    let rowEnd = 4;

    if (tableExpanded || isReplayActive) {
      rowStart = rowStart - 1;
      rowEnd = rowEnd - 1;
    }

    if (isReplayActive && tableExpanded) {
      colEnd = colEnd - 1;
    }

    if (isSidebarActive) {
      colEnd = colEnd - 1;
    }

    if (isTargetPanelActive) {
      colEnd = colEnd - 1;
    }

    if (isPortPanelActive) {
      colEnd = colEnd - 1;
    }

    return `col-span-1 col-end-${colEnd} row-start-${rowStart} row-end-${rowEnd}`;
  }, [
    isPortPanelActive,
    isReplayActive,
    isSidebarActive,
    isTargetPanelActive,
    tableExpanded,
  ]);

  const getReportLegendGridClass = useCallback(() => {
    let colEnd = 7;
    let colSpan = 4;

    if (isTargetPanelActive) {
      colEnd = colEnd - 1;
      colSpan = colSpan - 1;
    }

    if (isPortPanelActive) {
      colEnd = colEnd - 1;
      colSpan = colSpan - 1;
    }

    if (isSidebarActive) {
      colEnd = colEnd - 1;
      colSpan = colSpan - 1;
    }

    return `col-span-${colSpan} col-end-${colEnd} row-start-1 row-end-2`;
  }, [isPortPanelActive, isSidebarActive, isTargetPanelActive]);

  const getReportTableGridClass = useCallback(() => {
    let colEnd = 7;

    if (isSidebarActive) {
      colEnd = colEnd - 1;
    }

    if (isTargetPanelActive) {
      colEnd = colEnd - 1;
    }

    if (isPortPanelActive) {
      colEnd = colEnd - 1;
    }

    return `col-start-1 col-end-${colEnd} row-start-2 row-end-4`;
  }, [isPortPanelActive, isSidebarActive, isTargetPanelActive]);

  const getTargetInfoGridClass = useCallback(() => {
    let colStart = 6;
    let colEnd = 7;

    if (isSidebarActive) {
      colStart = colStart - 1;
      colEnd = colEnd - 1;
    }

    return `col-start-${colStart} col-end-${colEnd} row-start-1 row-end-4`;
  }, [isSidebarActive]);

  const getPortInfoGridClass = useCallback(() => {
    let colStart = 6;
    let colEnd = 7;

    if (isSidebarActive) {
      colStart = colStart - 1;
      colEnd = colEnd - 1;
    }

    if (isTargetPanelActive) {
      colStart = colStart - 1;
      colEnd = colEnd - 1;
    }

    return `col-start-${colStart} col-end-${colEnd} row-start-1 row-end-4`;
  }, [isSidebarActive, isTargetPanelActive]);

  const getReplayInfoGridClass = useCallback(() => {
    let colStart = 6;
    let colEnd = 7;
    let rowStart = 3;
    let rowEnd = 4;

    if (isTargetPanelActive) {
      colStart = colStart - 1;
      colEnd = colEnd - 1;
    }

    if (isPortPanelActive) {
      colStart = colStart - 1;
      colEnd = colEnd - 1;
    }

    if (tableExpanded) {
      rowStart = rowStart - 1;
      rowEnd = rowEnd - 1;
    }

    return `col-start-${colStart} col-end-${colEnd} row-start-${rowStart} row-end-${rowEnd}`;
  }, [isPortPanelActive, isTargetPanelActive, tableExpanded]);

  const getMapPanelGridStyles = useCallback(() => {
    // Calculate the same values as before
    let fourthCol = 'auto';
    let fifthCol = 'auto';
    let sixthCol = 'auto';

    if (isSidebarActive) {
      sixthCol = '384px';
      if (isTargetPanelActive) {
        fifthCol = multipleTargets ? '488px' : '464px';
        if (isPortPanelActive) {
          fourthCol = multiplePorts ? '488px' : '464px';
        }
      } else if (isPortPanelActive) {
        fifthCol = multiplePorts ? '488px' : '464px';
      }
    } else if (isTargetPanelActive) {
      sixthCol = multipleTargets ? '488px' : '464px';
      if (isPortPanelActive) {
        fifthCol = multiplePorts ? '488px' : '464px';
        if (isReplayActive) {
          fourthCol = '560px';
        }
      } else if (isReplayActive) {
        fifthCol = '560px';
      }
    } else if (isPortPanelActive) {
      sixthCol = multiplePorts ? '488px' : '464px';
      if (isReplayActive) {
        fifthCol = '560px';
      }
    } else if (isReplayActive) {
      sixthCol = '560px';
    }

    return {
      display: 'grid',
      gridTemplateColumns: `auto 1fr auto ${fourthCol} ${fifthCol} ${sixthCol}`,
      gridTemplateRows: `auto 1fr ${
        tableExpanded ? `${tableHeight}px` : 'auto'
      }`,
    };
  }, [
    isPortPanelActive,
    isReplayActive,
    isSidebarActive,
    isTargetPanelActive,
    multiplePorts,
    multipleTargets,
    tableExpanded,
    tableHeight,
  ]);
  return {
    getLayerControllerGridClass,
    getMetadataGridClass,
    getReportLegendGridClass,
    getReportTableGridClass,
    getTargetInfoGridClass,
    getPortInfoGridClass,
    getReplayInfoGridClass,
    getMapPanelGridStyles,
  };
};

export const useGridClasses = (): GridClasses => {
  const { activeTab, centerComponent, tableExpanded, reportDetailsExpanded } =
    useMapPanelContext();
  const { targetInfoDetailsList, selectedPorts } = useSelectionState();

  const isTableExpandedAndActive = useMemo(() => {
    if (!tableExpanded) {
      return false;
    }

    if (centerComponent === CENTERCOMPONENTS.NONE) {
      return false;
    }

    return true;
  }, [tableExpanded, centerComponent]);

  const isReplayActive = useMemo(() => {
    return activeTab === ACTIVETAB.REPLAY;
  }, [activeTab]);

  const isSidebarActive = useMemo(() => {
    if (activeTab === ACTIVETAB.NONE) {
      return false;
    }

    if (activeTab === ACTIVETAB.REPLAY) {
      return false;
    }

    // This is where the report sidebar is active, but hidden
    if (!reportDetailsExpanded) {
      return false;
    }

    return true;
  }, [activeTab, reportDetailsExpanded]);

  const isTargetPanelActive = useMemo(() => {
    if (targetInfoDetailsList.length === 0) {
      return false;
    }

    return true;
  }, [targetInfoDetailsList]);

  const isPortPanelActive = useMemo(() => {
    if (selectedPorts.length === 0) {
      return false;
    }

    return true;
  }, [selectedPorts]);

  const {
    getLayerControllerGridClass,
    getMetadataGridClass,
    getReportLegendGridClass,
    getReportTableGridClass,
    getMapPanelGridStyles,
    getReplayInfoGridClass,
    getTargetInfoGridClass,
    getPortInfoGridClass,
  } = useGridClassCalculations(
    isTableExpandedAndActive,
    isSidebarActive,
    isReplayActive,
    isTargetPanelActive,
    isPortPanelActive,
    targetInfoDetailsList.length > 1,
    selectedPorts.length > 1
  );

  const [layerControllerGrid, setLayerControllerGrid] = useState<string>(
    getLayerControllerGridClass()
  );

  const [metadataGrid, setMetadataGrid] = useState<string>(
    getMetadataGridClass()
  );

  const [reportLegendGrid, setReportLegendGrid] = useState<string>(
    getReportLegendGridClass()
  );

  const [reportTableGrid, setReportTableGrid] = useState<string>(
    getReportTableGridClass()
  );

  const [portInfoGrid, setPortInfoGrid] = useState<string>(
    getPortInfoGridClass()
  );

  const [targetInfoGrid, setTargetInfoGrid] = useState<string>(
    getTargetInfoGridClass()
  );

  const [replayGrid, setReplayGrid] = useState<string>(
    getReplayInfoGridClass()
  );

  const [mapPanelGrid, setMapPanelGrid] = useState<GridStyle>(
    getMapPanelGridStyles()
  );

  useEffect(() => {
    setMapPanelGrid((prevMapPanelGrid) => {
      const newMapPanelGrid = getMapPanelGridStyles();
      return prevMapPanelGrid !== newMapPanelGrid
        ? newMapPanelGrid
        : prevMapPanelGrid;
    });

    setLayerControllerGrid((prevLayerControllerGrid) => {
      const newLayerControllerGrid = getLayerControllerGridClass();
      return prevLayerControllerGrid !== newLayerControllerGrid
        ? newLayerControllerGrid
        : prevLayerControllerGrid;
    });

    setMetadataGrid((prevMetadataGrid) => {
      const newMetadataGrid = getMetadataGridClass();
      return prevMetadataGrid !== newMetadataGrid
        ? newMetadataGrid
        : prevMetadataGrid;
    });

    setReportLegendGrid((prevReportLegendGrid) => {
      const newReportLegendGrid = getReportLegendGridClass();
      return prevReportLegendGrid !== newReportLegendGrid
        ? newReportLegendGrid
        : prevReportLegendGrid;
    });

    setReportTableGrid((prevReportTableGrid) => {
      const newReportTableGrid = getReportTableGridClass();
      return prevReportTableGrid !== newReportTableGrid
        ? newReportTableGrid
        : prevReportTableGrid;
    });

    setPortInfoGrid((prevPortInfoGrid) => {
      const newPortInfoGrid = getPortInfoGridClass();
      return prevPortInfoGrid !== newPortInfoGrid
        ? newPortInfoGrid
        : prevPortInfoGrid;
    });

    setTargetInfoGrid((prevTargetInfoGrid) => {
      const newTargetInfoGrid = getTargetInfoGridClass();
      return prevTargetInfoGrid !== newTargetInfoGrid
        ? newTargetInfoGrid
        : prevTargetInfoGrid;
    });

    setReplayGrid((prevReplayGrid) => {
      const newReplayGrid = getReplayInfoGridClass();
      return prevReplayGrid !== newReplayGrid ? newReplayGrid : prevReplayGrid;
    });
  }, [
    getLayerControllerGridClass,
    getMapPanelGridStyles,
    getMetadataGridClass,
    getPortInfoGridClass,
    getReplayInfoGridClass,
    getReportLegendGridClass,
    getReportTableGridClass,
    getTargetInfoGridClass,
  ]);

  return {
    layerControllerGrid,
    metadataGrid,
    reportLegendGrid,
    reportTableGrid,
    targetInfoGrid,
    mapPanelGrid,
    replayGrid,
    portInfoGrid,
  };
};
